//frontend/src/components/StatusCounters.js
// This React component is a presentational stats card, showing driver counts (Total, Active, Reserved, Banned) and optionally highlighting when the active count reaches the agency’s capacity.
import React from 'react';

/**
 * Presentational counters card.
 * Props:
 *  - title?: string
 *  - counts: { Total: number, Active: number, Reserved: number, Banned: number }
 *  - activeCap?: number | null  // if provided, shows "Active X / cap"
 *  - className?: string
 */
export default function StatusCounters({ title = 'Drivers', counts, activeCap = null, className = '' }) {
  const atLimit = Number.isFinite(activeCap) && counts?.Active >= activeCap;

  const Stat = ({ label, value, highlight = false }) => (
    <div
      className={`min-w-0 rounded-2xl border p-4 shadow-sm sm:p-5 ${
        highlight ? 'border-red-200 bg-red-50' : 'border-app-border bg-app-card'
      }`}
    >
      <div className={`text-sm font-semibold ${highlight ? 'text-red-700' : 'text-app-muted'}`}>{label}</div>
      <div
        className={`mt-3 break-words text-2xl font-bold leading-tight tabular-nums sm:mt-4 sm:text-3xl ${
          highlight ? 'text-red-700' : 'text-app-text'
        }`}
      >
        {value}
      </div>
    </div>
  );

  const safe = k => (counts && Number.isFinite(counts[k]) ? counts[k] : 0);

  const activeDisplay = Number.isFinite(activeCap)
    ? `${safe('Active')} / ${activeCap}`
    : `${safe('Active')}`;

  return (
    <div className={`mb-5 ${className}`}>
      {title && (
        <h3 className="mb-3 text-lg font-semibold text-app-text">{title}</h3>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Total Drivers" value={safe('Total')} />
        <Stat label="Active Now" value={activeDisplay} highlight={atLimit} />
        <Stat label="Reserved" value={safe('Reserved')} />
        <Stat label="Banned / Blocked" value={safe('Banned')} />
      </div>
    </div>
  );
}
